import React, { useState, useEffect, useMemo, useRef, startTransition } from "react";
import { Checkbox, FormControlLabel, TextField, Tooltip } from "@mui/material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import styles from "./index.module.css"; // optional for styling
import CustomTextField from "../../CustomTextField";
import CustomDropdown from "../../CustomDropdownWithPlus";
import FilesPopup from "../FilesPopup";
import BrushIcon from "@mui/icons-material/Brush";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { GlobalWorkerOptions, getDocument } from "pdfjs-dist";
import CloseIcon from "@mui/icons-material/Close";
import { useParams } from "react-router-dom";
import { useInstanceImprintTypes } from "../../../helpers/instanceTerms";
import {
  buildArtworkSelectionPayload,
  getArtworkPreviewUrl,
} from "../artworkFileUtils";

GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

const arrayOfProjectFiles = [
  "https://media.istockphoto.com/id/1389438281/vector/realistic-white-baseball-cap-mockup-3d-sports-headgear-with-sun-protection-visor-blank.jpg?s=612x612&w=0&k=20&c=COXPYW7D5n52Cq2zrneiGwNdxjaB-qu9lCtXZ2XqT1U=",
  "https://media.istockphoto.com/id/842700386/photo/blank-black-baseball-hat-4-view-on-white-background.jpg?s=612x612&w=0&k=20&c=0fKtnDKbmrZGU8X2ZekfpTds8UobBSlndq34PCTxBWI=",
  "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQvEd6Z5K0paqS-ATqtzYPLlZ3oiNyuLkSovQ&s",
  "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRvxYkk3ZiYsAqRXnmQQbyCLXiireTgC9_1Sg&s",
];

const client = `${process.env.REACT_APP_NODEJS_SERVER_URL}/v1/`;

const shouldTriggerDialogSaveOnEnter = (event) => {
  if (event.key !== "Enter") return false;
  if (event.shiftKey || event.ctrlKey || event.altKey || event.metaKey) return false;
  if (event.defaultPrevented || event.nativeEvent?.isComposing) return false;

  const target = event.target;
  if (!target) return true;

  const tagName = String(target.tagName || "").toUpperCase();
  if (tagName === "TEXTAREA") return false;

  if (target.isContentEditable) return false;
  if (target.getAttribute?.("aria-multiline") === "true") return false;
  if (target.closest?.('[role="textbox"][aria-multiline="true"]')) return false;

  return true;
};

// simple in-memory cache so we don’t re-render thumbs repeatedly
const pdfThumbCache = new Map(); // key: src -> dataURL

const PdfThumb = ({ src, width = 132, height = 132 }) => {
  const [dataUrl, setDataUrl] = useState(() => pdfThumbCache.get(src) || "");
  const [failed, setFailed] = useState(false);
  const startedRef = useRef(false);
  useEffect(() => {
    let cancelled = false;
    if (dataUrl || startedRef.current) return;
    startedRef.current = true;

    (async () => {
      try {
        const loadingTask = getDocument({ url: src, withCredentials: false });
        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1);

        const v0 = page.getViewport({ scale: 1 });
        const scale = Math.min(width / v0.width, height / v0.height);
        const viewport = page.getViewport({ scale: Math.max(scale, 0.2) });

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);

        await page.render({ canvasContext: ctx, viewport }).promise;
        if (cancelled) return;
        const url = canvas.toDataURL("image/png");
        pdfThumbCache.set(src, url);
        setDataUrl(url);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => { cancelled = true; };
  }, [src, width, height, dataUrl]);

  if (failed) return null;
  if (!dataUrl) {
    return (
      <div style={{ width, height, display: "grid", placeItems: "center", fontSize: 11, color: "#888" }}>
        loading…
      </div>
    );
  }
  return <img src={dataUrl} alt="PDF preview" style={{ width, height, objectFit: "contain" }} loading="eager" decoding="async" />;
};


/* helper: make {id,name} and dedupe by lowercase name */
function toOptionsUnique(names) {
  const map = new Map();
  names.forEach((n) => {
    if (!n) return;
    const name = String(n).trim();
    if (!name) return;
    const key = name.toLowerCase();
    if (!map.has(key)) map.set(key, { id: name, name });
  });
  return Array.from(map.values());
}

// helpers for image/file display from artwork
const IMG_EXT_RX = /\.(png|jpe?g|gif|webp|bmp|svg)$/i;


const getFileName = (url = "") => {
  try {
    const u = new URL(url, window.location.origin);
    return decodeURIComponent(u.pathname.split("/").pop() || "File");
  } catch {
    const parts = String(url).split(/[\/?#]/);
    return decodeURIComponent(parts.pop() || "File");
  }
};
const getExt = (url = "") => {
  const m = String(url).match(/\.([a-z0-9]+)(?:\?|#|$)/i);
  return m ? m[1].toUpperCase() : "FILE";
};

const ArtworkUploadModal = ({
  open,
  onClose,
  onUpload,
  formData,
  onFormChange,
  onSelectImage,
  orderItem,
  selectedImage,
  clientFiles,
  handleDone,
  rows = {},
  customerId,
  decorationSource,
  imprintSizes = [],
  imprintColors = [],
  imprintLocations = [],
  sourceOptions = [],
}) => {
  const { instanceUniqueId } = useParams();
  const { imprintTypes } = useInstanceImprintTypes(instanceUniqueId);
  const methodDisplayName = rows?.DecoratorDetails?.methodName || "Unknown";
  const isSupplierCustom =
  rows?.DecoratorDetails?.source === "supplier-custom" ||
  decorationSource === "supplier-custom";
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [imgBroken, setImgBroken] = useState(false);
  const [logoNameDraft, setLogoNameDraft] = useState(() => formData?.logoName ?? "");
  // whenever selected image changes, clear the broken flag
  useEffect(() => {
    setImgBroken(false);
  }, [formData?.selectedImage]);

  useEffect(() => {
    setLogoNameDraft(formData?.logoName ?? "");
  }, [open, formData?.logoName]);

  const options = [
    { id: 1, name: "Yes" },
    { id: 2, name: "No" },
  ];
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    onFormChange(name, value);

    if (errors[name] && value?.trim()) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleLogoNameChange = (e) => {
    const nextValue = e.target.value;
    setLogoNameDraft(nextValue);
    startTransition(() => {
      onFormChange("logoName", nextValue);
    });

    if (errors.logoName && nextValue?.trim()) {
      setErrors((prev) => ({ ...prev, logoName: null }));
    }
  };

  useEffect(() => {
    const v = formData?.proofRequiredAction;
    if (v === undefined || v === null || v === '') {
      onFormChange('proofRequiredAction', 1); // 1 = Designer required
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]); // run each time the dialog opens

  const handleSelect = (event, name) => {
    const value = event.target.value;
    if (name === "imprintType") {
      const selectedMethod = orderItem?.Imprinting?.Methods?.Values?.find(
        (method) => method.Code === value
      );
      let charge = 0;
      if (selectedMethod?.Charges?.length > 0) {
        const firstCharge = selectedMethod.Charges[0];
        const firstPrice = firstCharge?.Prices?.[0]?.Price;
        if (typeof firstPrice === "number") {
          charge = firstPrice;
        }
      }
      onFormChange(name, value, charge);
    } else {
      onFormChange(name, value);
    }
    if (errors[name] && value) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSave = () => {
    const requiredFields = [
      "imprintType",
      "proofRequired",
      "proofRequiredAction",
      "logoSize",
      "logoColor",
    ];
    const newErrors = {};

    if (!logoNameDraft || logoNameDraft === "") {
      newErrors.logoName = "This logoName field is required";
    }

    requiredFields.forEach((field) => {
      const value = formData?.[field];
      if (value === undefined || value === null || value === "") {
        newErrors[field] = `This ${field} field is required`;
      }
    });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (formData?.logoName !== logoNameDraft) {
      onFormChange("logoName", logoNameDraft);
    }

    onClose(); // proceed if valid
  };

  const proofRequiredActionOptions = [
    { id: 0, name: "None" },
    { id: 1, name: "Designer required" },
    { id: 2, name: "Supplier provided" },
  ];

  const proofRequiredOptions = [
    { id: 0, name: "No proof required" },
    { id: 1, name: "Email Proof" },
    { id: 2, name: "Pre Production Sample" },
    { id: 3, name: "Digital Photo" },
  ];

  const handleClosePopup = () => {
    setPopupOpen(false);
  };
  const handleOpenFilePopup = () => {
    setPopupOpen(true);
  };

  const handleDialogClose = (_, reason) => {
    if (popupOpen) return;
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
    onClose();
  };

  const handleDialogKeyDown = (event) => {
    if (!shouldTriggerDialogSaveOnEnter(event)) return;

    event.preventDefault();
    handleSave();
  };

  /* ------------------------------------------------------------------
     ✨ UPDATED: Build imprint options and PRIORITIZE supplier methods.
     Supplier (from orderItem) → then others A→Z. De-duped by name.
  ------------------------------------------------------------------- */
  const imprintOptions = useMemo(() => {
    const supplierMethodOptions =
      (orderItem?.Imprinting?.Methods?.Values || [])
        .map((m) => {
          const name = (m.Name ?? m.Code ?? m.Value ?? "").toString().trim();
          const id = (m.Code ?? m.Name ?? m.Value ?? name).toString().trim();
          const isSupplier = /\(supplier\)/i.test(name); // ✅ detect "(Supplier)"
          return name ? { id, name, isSupplier } : null;
        })
        .filter(Boolean);

    const supplierKeys = new Set(
      supplierMethodOptions.map((o) => o.name.toLowerCase())
    );

    const sourceOpts = (sourceOptions || [])
      .map((o) => {
        const name = (o.label ?? o.name ?? o.value ?? "").toString().trim();
        const id = (o.value ?? o.id ?? name).toString().trim();
        const isSupplier = /\(supplier\)/i.test(name);
        return name ? { id, name, isSupplier } : null;
      })
      .filter(Boolean);

    const baseOpts = toOptionsUnique(imprintTypes).map((o) => ({
      ...o,
      isSupplier: /\(supplier\)/i.test(o.name), // ✅ check base list too
    }));

    // merge & dedupe by lowercase name — prefer supplier variant if present
    const byName = new Map();
    [...supplierMethodOptions, ...sourceOpts, ...baseOpts].forEach((opt) => {
      if (!opt) return;
      const key = opt.name.toLowerCase();
      const existing = byName.get(key);
      if (!existing || (opt.isSupplier && !existing.isSupplier)) {
        byName.set(key, opt);
      }
    });

    // ✅ supplier entries first, then everything else A→Z
    return Array.from(byName.values()).sort((a, b) => {
      if (a.isSupplier && !b.isSupplier) return -1;
      if (!a.isSupplier && b.isSupplier) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [imprintTypes, orderItem, sourceOptions]);

  return (
    <Dialog open={open} onClose={handleDialogClose} onKeyDown={handleDialogKeyDown} fullWidth maxWidth="md">
      <DialogTitle className="create-order-dialog-title"   sx={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    pr: 1,
  }}>
        Upload Artwork

        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={{ color: "#FFF" }}
        >
          <CloseIcon />
        </IconButton>

      </DialogTitle>
      <DialogContent className="create-order-dialog-content upload-artwork-popup">
        <Box display="flex" gap={3}>
          {/* Left: Fields */}
          <Box flex={1}>
            <Box className="field">
              <ImprintTypeField
                imprintOptions={imprintOptions}
                formData={formData}
                onFormChange={onFormChange}
                errors={errors}
                forceOverride={isSupplierCustom}
              />
            </Box>
            <Box className="field">
              <CustomDropdown
                label="Proof Required"
                name="proofRequired"
                options={proofRequiredOptions}
                handleSelect={handleSelect}
                selectedValue={formData?.proofRequired}
                error={Boolean(errors.proofRequired)}
                helperText={errors.proofRequired}
              />
            </Box>
            <Box className="field">
              <CustomTextField
                label="Logo Name"
                error={Boolean(errors.logoName)}
                helperText={errors.logoName}
                name="logoName"
                placeholder="Enter logo name"
                value={logoNameDraft}
                handleChange={handleLogoNameChange}
              />
            </Box>
            <Box className="field">
              <CustomTextField
                label="Logo Size"
                error={Boolean(errors.logoSize)}
                helperText={errors.logoSize}
                name="logoSize"
                placeholder="Enter logo size"
                value={formData?.logoSize}
                handleChange={handleInputChange}
              />
            </Box>
            {imprintSizes.length > 0 ? (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                Suggestions: {imprintSizes.map((s) => s.label).join(", ")}
              </Typography>
            ) : (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                No imprint size recommended by supplier.
              </Typography>
            )}
            <Box className="field">
              <CustomTextField
                label="Logo Color"
                error={Boolean(errors.logoColor)}
                helperText={errors.logoColor}
                name="logoColor"
                placeholder="Enter logo color"
                value={formData?.logoColor}
                handleChange={handleInputChange}
              />
            </Box>
            {imprintColors.length > 0 ? (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                Suggestions: {imprintColors.map((c) => c.label).join(", ")}
              </Typography>
            ) : (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                No imprint colour recommended by supplier.
              </Typography>
            )}

            <Box className="field">
              <CustomDropdown
                label="Repeat Logo?"
                name="repeatLogo"
                options={options}
                handleSelect={handleSelect}
                selectedValue={formData?.repeatLogo}
              />
            </Box>
            <Box className="field">
              <CustomTextField
                label="Notes"
                name="supplierNotes"
                placeholder="Enter notes"
                value={formData?.supplierNotes}
                handleChange={handleInputChange}
                multiline
                minRows={5}
                maxRows={12}
              />
            </Box>
          </Box>

          {/* Right: Image Upload */}
          <Box flex={1} className={styles.uploadArea}>
          <Box className={styles.imageContainer}>
  {(() => {
    const originalSrc = formData?.selectedImage || "";
    const preferredPreview = getArtworkPreviewUrl(formData) || "";
    const previewFailed = Boolean(imgBroken && preferredPreview);
    const previewSrc = previewFailed ? (originalSrc || preferredPreview) : (preferredPreview || originalSrc);
    const hasSrc = !!previewSrc;
    const isImg = IMG_EXT_RX.test(previewSrc) && (!imgBroken || previewFailed);
    const isPdf = getExt(previewSrc).toLowerCase() === "pdf";

    const label = rows?.DecoratorDetails?.methodName || "Artwork";
    const title = hasSrc
      ? `${getFileName(originalSrc || previewSrc)} • ${getExt(originalSrc || previewSrc)}`
      : "Select an image";

    if (!hasSrc) {
      return (
        <Box className={styles.dottedBox} onClick={handleOpenFilePopup}>
          Select Image
        </Box>
      );
    }

    const Wrapper = ({ children }) => (
      <Tooltip title={title}>
        <Box
          className={styles.imagePreview}
          onClick={handleOpenFilePopup}
          sx={{
            border: "1px dashed #bdbdbd",
            borderRadius: "8px",
            width: 132,
            height: 132,
            overflow: "hidden",
            cursor: "pointer",
            bgcolor: "#fafafa",
            position: "relative",
          }}
        >
          <IconButton
            size="small"
            aria-label="Preview file"
            title="Preview"
            onClick={(e) => {
              e.stopPropagation();
              window.open(originalSrc || previewSrc, "_blank", "noopener,noreferrer");
            }}
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              zIndex: 2,
              bgcolor: "white",
              boxShadow: 1,
              "&:hover": { bgcolor: "white" },
            }}
          >
            <VisibilityOutlinedIcon fontSize="small" />
          </IconButton>
          {children}
        </Box>
      </Tooltip>
    );

    if (isImg) {
      return (
        <Wrapper>
            <img
            src={previewSrc}
            alt={label}
            className={styles.selectedImage}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
            onError={() => setImgBroken(true)}
            decoding="async"
            loading="eager"
          />
        </Wrapper>
      );
    }

    if (isPdf) {
      return (
        <Wrapper>
          <PdfThumb src={previewSrc} width={132} height={132} />
        </Wrapper>
      );
    }

    return (
      <Wrapper>
        <Box
          sx={{
            textAlign: "center",
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            pointerEvents: "none",
            gap: 0.5,
          }}
        >
          <InsertDriveFileOutlinedIcon />
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {getExt(originalSrc || previewSrc)}
          </Typography>
        </Box>
      </Wrapper>
    );
  })()}
</Box>


            <Box className={`field ${styles.hiddenProofAction}`} sx={{ display: "none", mt: "10px", ml: "30px" }}>
              <span className={styles.brushLabel}>
                <BrushIcon className={styles.brushIcon} />
                Artwork Proof Action
              </span>
              <CustomDropdown
                label=""
                name="proofRequiredAction"
                options={proofRequiredActionOptions}
                handleSelect={handleSelect}
                selectedValue={formData.proofRequiredAction}
                error={!!errors.proofRequiredAction}
                helperText={errors.proofRequiredAction}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <FilesPopup
          open={popupOpen}
          onClose={handleClosePopup}
          customerId={customerId}
          orderItem={orderItem}
          imageGroups={{ "Client Files": clientFiles || [] }}
          handleDone={handleDone}
          onImageSelect={(val) => {
            if (typeof val === "string") {
              onSelectImage(val);
            }
          }}
          onSelectFile={(file) => {
            const selection = buildArtworkSelectionPayload(file);
            if (selection.originalUrl) {
              onSelectImage(selection.originalUrl);
            }
            onUpload(selection);
          }}

          selectedImage={formData?.selectedImage}
        />


      <Box className={styles.buttonContainer}>
        <Button onClick={handleSave}>Save</Button>
      </Box>
    </Dialog>
  );
};

function ImprintTypeField({ imprintOptions, formData, onFormChange, errors, forceOverride = false }) {
  const isCustomValue =
    !!formData.imprintType &&
    !imprintOptions.some(
      (opt) => opt.id === formData.imprintType || opt.name === formData.imprintType
    );

  const override = forceOverride || isCustomValue;

  const handleDropdown = (event, name) => {
    onFormChange(name, event.target.value);
  };

  return (
    <Box className="field">
      {override ? (
        <TextField
          fullWidth
          label="Imprint Type (custom)"
          value={formData.imprintType || ""}
          onChange={(e) => onFormChange("imprintType", e.target.value)}
          error={Boolean(errors.imprintType)}
          helperText={errors.imprintType}
          placeholder="Describe the supplier custom request…"
        />
      ) : (
        <CustomDropdown
          label="Imprint Type"
          name="imprintType"
          options={imprintOptions}
          handleSelect={handleDropdown}
          selectedValue={formData.imprintType}
          error={Boolean(errors.imprintType)}
          helperText={errors.imprintType}
        />
      )}
    </Box>
  );
}


export default ArtworkUploadModal;
